import { FC, ReactElement } from 'react';

import { skipToken } from '@reduxjs/toolkit/query';

import { RaaExpandablePreview } from 'raa-react';

import BebyggelseFastigheterSection from 'components/bebyggelseobjekt/sections/BebyggelseFastigheterSection';
import BebyggelseHistorikSection from 'components/bebyggelseobjekt/sections/BebyggelseHistorikSection';
import RegistreringshistorikSection from 'components/bebyggelseobjekt/sections/RegistreringshistorikSection';
import SammansattBebyggelseIngaendeObjektSection from 'components/bebyggelseobjekt/sections/SammansattBebyggelseIngaendeObjektSection';
import SammansattBebyggelseSkyddbestammelserSection from 'components/bebyggelseobjekt/sections/SammansattBebyggelseSkyddbestammelserSection';
import SammansattBebyggelseSkyddstypSection from 'components/bebyggelseobjekt/sections/SammansattBebyggelseSkyddstypSection';
import SammansattBebyggelseTitleSection from 'components/bebyggelseobjekt/sections/SammansattBebyggelseTitleSection';
import BebyggelseDokumentationSection from 'components/bebyggelseobjekt/sections/dokumentation/BebyggelseDokumentationSection';
import {
  DrawerBody,
  DrawerContainer,
  DrawerSection,
  NavigationDrawerHead
} from 'components/drawerSections/DrawerSections';
import UtkastStatusSection from 'components/utkast/utkastPresentation/sections/UtkastStatusSection';

import { BebyggelseobjektDomaner, BebyggelseobjektUtkast } from 'models';

import { useGetDokumentationQuery } from 'api/endpoints/dokumentationSlice';
import { renderWithParagraphs } from 'utilities/renderWithParagraphs';

type UtkastSammansattPresentationProps = { utkastData: BebyggelseobjektUtkast; domaner?: BebyggelseobjektDomaner };

const UtkastSammansattPresentation: FC<UtkastSammansattPresentationProps> = ({ domaner, utkastData }): ReactElement => {
  // Initering för att veta hur många dokument som finns i mellanregistret
  const { data: dokumentation } = useGetDokumentationQuery(utkastData.utkast_id ?? skipToken);

  return (
    <DrawerContainer>
      <NavigationDrawerHead />
      <DrawerBody>
        <UtkastStatusSection utkast={utkastData} />
        <SammansattBebyggelseTitleSection utkast={utkastData} />
        <SammansattBebyggelseSkyddstypSection utkast={utkastData} />
        <SammansattBebyggelseSkyddbestammelserSection
          skyddsbestammelser={utkastData.sammansatt?.skyddsbestammelser}
          sammansattParentId={utkastData.sammansatt_parent_id}
        />
        <SammansattBebyggelseIngaendeObjektSection
          ingaendeObject={utkastData.sammansatt?.ingaendeobjekt}
          isPublicerad={false}
          havtSkydd={utkastData.sammansatt?.havt_skydd}
          skyddstyp={utkastData.skydd.skyddstyp}
        />

        <DrawerSection>
          <h2 className="tw-heading-md tw-mb-4">Beskrivning</h2>
          <RaaExpandablePreview ariaLabel="Beskrivning av sammansatt utkast">
            <>{renderWithParagraphs(utkastData.beskrivning || '')}</>
          </RaaExpandablePreview>
        </DrawerSection>

        <BebyggelseHistorikSection
          historik={utkastData.historik || ''}
          handelser={utkastData.handelser}
          domaner={domaner}
        />

        <BebyggelseDokumentationSection
          uri={utkastData.publicerad_uri}
          utkastId={utkastData.utkast_id}
          includeRitningar={true}
          totalDocuments={
            dokumentation
              ? dokumentation.length + utkastData.dokumentation_ref.arkiv_ref.length
              : utkastData.dokumentation_ref.arkiv_ref.length
          }
        />

        <BebyggelseFastigheterSection fastigheter={utkastData.berorda_fastigheter} />

        <DrawerSection>
          <h2 className="tw-heading-md tw-mb-4">Referenser</h2>
          {renderWithParagraphs(utkastData.referenser || '–')}
        </DrawerSection>

        <RegistreringshistorikSection bebyggelseobjektId={utkastData.utkast_id} />
      </DrawerBody>
    </DrawerContainer>
  );
};

export default UtkastSammansattPresentation;
